import type { ExerciseDef } from './types'

// ─── 5.1 Chance, counted ──────────────────────────────────────────────────────

export const simulateCoin: ExerciseDef = {
  starter: `// random.random() gives a number between 0 and 1. "< 0.5" happens half the time.
let heads = 0
const flips = 10000
for (let i = 0; i < flips; i++) {
  if (random.random() < 0.5) heads++
}
console.log("fraction of heads:", heads / flips)
`,
}

export const diceSeven: ExerciseDef = {
  starter: `// Estimate the probability that two dice add up to 7 — by simulation.
// random.int(1, 7) gives a whole number from 1 to 6.
function estimateSeven(rolls: number): number {
  let sevens = 0
  // roll two dice \`rolls\` times; count the sevens
  return sevens / rolls
}

console.log(estimateSeven(100000), "vs exact", 6 / 36)
`,
  solution: `function estimateSeven(rolls: number): number {
  let sevens = 0
  for (let i = 0; i < rolls; i++) {
    const a = random.int(1, 7)
    const b = random.int(1, 7)
    if (a + b === 7) sevens++
  }
  return sevens / rolls
}

console.log(estimateSeven(100000), "vs exact", 6 / 36)
`,
  tests: `
test("the estimate is within 0.01 of 1/6", () => expect(estimateSeven(100000), "estimateSeven(100000)").toBeCloseTo(1 / 6, 0.01))
test("it returns a fraction between 0 and 1", () => expect(estimateSeven(500), "estimateSeven(500)").toBeBetween(0.05, 0.3))
`,
}

export const exactCount: ExerciseDef = {
  starter: `// Exact probability by counting: list every equally likely outcome.
// probabilitySum(target): chance that two dice sum to target.
function probabilitySum(target: number): number {
  let favourable = 0
  let total = 0
  // loop over every die a from 1..6 and die b from 1..6
  return favourable / total
}

console.log(probabilitySum(7), probabilitySum(2), probabilitySum(12))
`,
  solution: `function probabilitySum(target: number): number {
  let favourable = 0
  let total = 0
  for (let a = 1; a <= 6; a++) {
    for (let b = 1; b <= 6; b++) {
      total++
      if (a + b === target) favourable++
    }
  }
  return favourable / total
}

console.log(probabilitySum(7), probabilitySum(2), probabilitySum(12))
`,
  tests: `
test("P(sum = 7) is 6/36", () => expect(probabilitySum(7), "probabilitySum(7)").toBeCloseTo(6 / 36))
test("P(sum = 2) is 1/36", () => expect(probabilitySum(2), "probabilitySum(2)").toBeCloseTo(1 / 36))
test("P(sum = 13) is 0", () => expect(probabilitySum(13), "probabilitySum(13)").toBeCloseTo(0))
test("all sums together have probability 1", () => {
  let s = 0
  for (let t = 2; t <= 12; t++) s += probabilitySum(t)
  expect(s, "sum over every target").toBeCloseTo(1)
})
`,
}

// ─── 5.2 Distributions ────────────────────────────────────────────────────────

export const histogram: ExerciseDef = {
  starter: `// Sample 5000 numbers from a bell curve and draw their histogram.
const samples = range(5000).map(() => random.normal(10, 2))

const bins = vec.zeros(20)                 // 20 buckets covering 0 … 20
for (const s of samples) {
  const b = Math.floor(s)
  if (b >= 0 && b < 20) bins[b]++
}
plot.bar(bins, { title: "5000 samples of normal(mean 10, std 2)" })
console.log("mean ≈", vec.mean(samples))
`,
}

export const varianceStd: ExerciseDef = {
  starter: `// variance(xs): the average of (x − mean)²
// std(xs): the square root of the variance
function variance(xs: number[]): number {
  return 0
}

function std(xs: number[]): number {
  return 0
}

console.log(variance([2, 4, 4, 4, 5, 5, 7, 9]), std([2, 4, 4, 4, 5, 5, 7, 9]))
`,
  solution: `function variance(xs: number[]): number {
  const m = vec.mean(xs)
  let total = 0
  for (const x of xs) total += (x - m) ** 2
  return total / xs.length
}

function std(xs: number[]): number {
  return Math.sqrt(variance(xs))
}

console.log(variance([2, 4, 4, 4, 5, 5, 7, 9]), std([2, 4, 4, 4, 5, 5, 7, 9]))
`,
  tests: `
test("variance of the example is 4", () => expect(variance([2, 4, 4, 4, 5, 5, 7, 9]), "variance(...)").toBeCloseTo(4))
test("std of the example is 2", () => expect(std([2, 4, 4, 4, 5, 5, 7, 9]), "std(...)").toBeCloseTo(2))
test("identical values have no spread", () => expect(variance([3, 3, 3]), "variance([3, 3, 3])").toBeCloseTo(0))
test("std of normal(0, 3) samples is about 3", () => {
  const xs = range(20000).map(() => random.normal(0, 3))
  expect(std(xs), "std of 20000 samples").toBeCloseTo(3, 0.1)
})
`,
}

export const standardize: ExerciseDef = {
  starter: `// "Standardising" data: subtract the mean, divide by the std.
// Afterwards every feature has mean 0 and std 1, which makes training much smoother.
function standardize(xs: number[]): number[] {
  return xs
}

const sizes = [60, 95, 120, 150, 210]
console.log(standardize(sizes))
`,
  solution: `function standardize(xs: number[]): number[] {
  const m = vec.mean(xs)
  const s = Math.sqrt(vec.mean(xs.map(x => (x - m) ** 2)))
  return xs.map(x => (x - m) / s)
}

const sizes = [60, 95, 120, 150, 210]
console.log(standardize(sizes))
`,
  tests: `
test("the result has mean 0", () => expect(vec.mean(standardize([60, 95, 120, 150, 210])), "mean of result").toBeCloseTo(0))
test("the result has std 1", () => {
  const z = standardize([60, 95, 120, 150, 210])
  expect(Math.sqrt(vec.mean(z.map(v => v * v))), "std of result").toBeCloseTo(1)
})
test("order is preserved", () => {
  const z = standardize([3, 1, 2])
  expect(z[0] > z[2] && z[2] > z[1], "z[0] > z[2] > z[1]").toBeTruthy()
})
`,
}

// ─── 5.3 Softmax ──────────────────────────────────────────────────────────────

export const softmaxImpl: ExerciseDef = {
  starter: `// softmax(scores): exponentiate each score, then divide by the total.
// Subtract the biggest score first — it doesn't change the answer, but
// stops e^1000 from overflowing to Infinity.
function softmax(scores: number[]): number[] {
  return scores
}

console.log(softmax([2, 1, 0.1]))
console.log(softmax([1000, 1001]))   // shouldn't be NaN!
`,
  solution: `function softmax(scores: number[]): number[] {
  const m = Math.max(...scores)
  const exps = scores.map(s => Math.exp(s - m))
  const total = exps.reduce((a, b) => a + b, 0)
  return exps.map(e => e / total)
}

console.log(softmax([2, 1, 0.1]))
console.log(softmax([1000, 1001]))
`,
  tests: `
test("probabilities add up to 1", () => expect(vec.sum(softmax([2, 1, 0.1])), "sum of softmax([2, 1, 0.1])").toBeCloseTo(1))
test("matches the reference", () => expect(softmax([2, 1, 0.1]), "softmax([2, 1, 0.1])").toBeCloseTo([0.659001, 0.242433, 0.098566], 1e-5))
test("equal scores → equal probabilities", () => expect(softmax([3, 3, 3, 3]), "softmax([3, 3, 3, 3])").toBeCloseTo([0.25, 0.25, 0.25, 0.25]))
test("huge scores don't overflow", () => expect(softmax([1000, 1001]), "softmax([1000, 1001])").toBeCloseTo([0.268941, 0.731059], 1e-5))
`,
}

export const temperature: ExerciseDef = {
  starter: `// Temperature: divide the scores by T before softmax.
const scores = [2, 1, 0.1]
for (const T of [0.25, 0.5, 1, 2, 5]) {
  const p = vec.softmax(scores, T).map(v => round(v, 3))
  console.log("T =", T, " →", p)
}
`,
}

// ─── 5.4 Cross-entropy ────────────────────────────────────────────────────────

export const crossEntropy: ExerciseDef = {
  starter: `// 1. crossEntropy(probs, target): −ln(probability given to the true class)
function crossEntropy(probs: number[], target: number): number {
  return 0
}

// 2. crossEntropyFromLogits(scores, target): softmax first, then cross-entropy.
//    (vec.softmax is available.)
function crossEntropyFromLogits(scores: number[], target: number): number {
  return 0
}

console.log(crossEntropy([0.7, 0.2, 0.1], 0), crossEntropyFromLogits([0, 0, 0], 1))
`,
  solution: `function crossEntropy(probs: number[], target: number): number {
  return -Math.log(probs[target])
}

function crossEntropyFromLogits(scores: number[], target: number): number {
  return crossEntropy(vec.softmax(scores), target)
}

console.log(crossEntropy([0.7, 0.2, 0.1], 0), crossEntropyFromLogits([0, 0, 0], 1))
`,
  tests: `
test("a confident right answer has small loss", () => expect(crossEntropy([0.9, 0.05, 0.05], 0), "crossEntropy([0.9, 0.05, 0.05], 0)").toBeCloseTo(0.1053605, 1e-6))
test("a confident wrong answer has big loss", () => expect(crossEntropy([0.9, 0.05, 0.05], 1), "crossEntropy([0.9, 0.05, 0.05], 1)").toBeCloseTo(2.995732, 1e-6))
test("certainty on the right answer gives 0", () => expect(crossEntropy([0, 1, 0], 1), "crossEntropy([0, 1, 0], 1)").toBeCloseTo(0))
test("uniform guess over 3 classes is ln 3", () => expect(crossEntropyFromLogits([0, 0, 0], 1), "crossEntropyFromLogits([0, 0, 0], 1)").toBeCloseTo(Math.log(3)))
test("from logits matches by hand", () => expect(crossEntropyFromLogits([2, 1, 0.1], 0), "crossEntropyFromLogits([2, 1, 0.1], 0)").toBeCloseTo(0.4170300, 1e-6))
`,
}

export const tenClasses: ExerciseDef = {
  starter: `// A model that knows nothing gives every one of 10 classes probability 1/10.
const uniform = vec.fill(10, 1 / 10)
console.log("loss of pure guessing:", -Math.log(uniform[0]))
// Look at the "loss" meter at the top of this page...
`,
}
